import { skipToken } from '@reduxjs/toolkit/query';
import { useNavigate } from 'react-router-dom';

import { Avatar, Button, Typography } from '@/ui';

import { useGetAssistantCoachesQuery } from './api';
import { hasUnsettledAssistantBooking } from './operationRecords';
import { assistantBookPath } from './paths';
import { useAssistantSessionToken } from './session';
import { useEndAssistantSessionOnUnauthorized } from './useAssistantSession';

import type { AssistantCoach } from './api';
import type { AssistantOperationRecord } from './operationRecords';
import type { FC } from 'react';

const BOOKED_NOTES: Partial<Record<AssistantCoach['nextStep'], string>> = {
  chemistry_booked: 'Chemistry call booked',
  series_booked: 'Biweekly sessions booked',
};

const CoachActions: FC<{
  /** A booking with this coach has no answer yet: no second one until it does. */
  busy: boolean;
  coach: AssistantCoach;
  memberId: string;
}> = ({ busy, coach, memberId }) => {
  const navigate = useNavigate();
  if (busy) {
    return (
      <Typography color="text.secondary" variant="body2">
        Booking in progress…
      </Typography>
    );
  }
  if (coach.nextStep === 'not_bookable') {
    return (
      <Typography color="text.secondary" variant="body2">
        Contact Mento to book with {coach.firstName}: email concierge@mento.co.
      </Typography>
    );
  }
  const note = BOOKED_NOTES[coach.nextStep];
  return (
    <>
      {coach.nextStep === 'book_chemistry' ? (
        <Button
          size="small"
          variant="outlined"
          onClick={() =>
            navigate(assistantBookPath(memberId, coach.personId, 'chemistry'))
          }
        >
          Book chemistry call
        </Button>
      ) : null}
      {coach.nextStep === 'book_series' ? (
        <Button
          size="small"
          variant="outlined"
          onClick={() =>
            navigate(assistantBookPath(memberId, coach.personId, 'series'))
          }
        >
          Book biweekly sessions
        </Button>
      ) : null}
      {coach.canBookSingle ? (
        <Button
          size="small"
          variant="text"
          onClick={() =>
            navigate(assistantBookPath(memberId, coach.personId, 'single'))
          }
        >
          Book a single session
        </Button>
      ) : null}
      {note ? (
        <Typography color="text.secondary" variant="body2">
          {note}
        </Typography>
      ) : null}
    </>
  );
};

/**
 * The member's matched coaches and what the assistant can book with each
 * next, as the member's own coach cards show it.
 */
export const AssistantBookSection: FC<{
  memberId: string;
  memberName: string;
  records: AssistantOperationRecord[];
}> = ({ memberId, memberName, records }) => {
  const token = useAssistantSessionToken();
  const coaches = useGetAssistantCoachesQuery(token ? memberId : skipToken);
  const { refetch: refetchCoaches } = coaches;
  useEndAssistantSessionOnUnauthorized(coaches.error);

  if (coaches.isError) {
    return (
      <section aria-label="Book" className="flex flex-col gap-2">
        <Typography variant="body1">
          We couldn&apos;t load {memberName}&apos;s coaches.
        </Typography>
        <div>
          <Button variant="outlined" onClick={refetchCoaches}>
            Retry
          </Button>
        </div>
      </section>
    );
  }
  // Nothing to book with: no matched coach, or not loaded yet.
  if (!coaches.currentData?.length) return null;

  return (
    <section aria-label="Book" className="flex flex-col gap-3">
      <Typography variant="h4">Book for {memberName}</Typography>
      <ul className="flex flex-col gap-3">
        {coaches.currentData.map((coach) => (
          <li
            key={coach.personId}
            aria-label={`Coach ${coach.firstName}`}
            className="flex flex-col gap-2 rounded-xl border border-cement-300 p-4"
          >
            <div className="flex items-center gap-3">
              <Avatar
                alt=""
                firstName={coach.firstName}
                size="s"
                src={coach.pictureUrl ?? undefined}
              />
              <Typography variant="subtitle1">{coach.firstName}</Typography>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <CoachActions
                busy={hasUnsettledAssistantBooking(records, coach.personId)}
                coach={coach}
                memberId={memberId}
              />
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
};
